import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/vdp-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { Location, UserProfile } from '~/contracts';

type ContextType = {
  logInFlag: number;
  partnerId: number;
  sessionId: string;
  setUserLocation: React.Dispatch<React.SetStateAction<Partial<Location>>>;
  triggerFetchUserProfile: () => void;
  user: LoadableState<UserProfile>;
  userLocation: Partial<Location>;
};

const defaultContext: ContextType = {
  logInFlag: 0,
  partnerId: 1,
  sessionId: '',
  setUserLocation() {},
  triggerFetchUserProfile() {},
  user: defaultLoadableState<UserProfile>({
    email: '',
    isLoggedIn: false,
    userId: ''
  }),
  userLocation: {}
};

const UserContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  partnerId: number;
  sessionId: string;
  userId: string;
  userLocation: Partial<Location>;
}>;

export function UserContextProvider(props: OwnProps) {
  const [localUserRequestFlag, setLocalUserRequestFlag] = useState(0);
  const user = useLoadableState({
    email: '',
    isLoggedIn: false,
    userId: props.userId
  });
  const [userLocation, setUserLocation] = useState(props.userLocation);

  const triggerFetchUserProfile = useCallback(() => {
    setLocalUserRequestFlag(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      logInFlag: localUserRequestFlag,
      partnerId: props.partnerId,
      sessionId: props.sessionId,
      setUserLocation,
      triggerFetchUserProfile,
      user,
      userLocation
    }),
    [
      localUserRequestFlag,
      props.partnerId,
      props.sessionId,
      triggerFetchUserProfile,
      user,
      userLocation
    ]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

UserContextProvider.displayName = 'UserContextProvider';

export function useUser(): LoadableState<UserProfile> {
  const { user } = useContext(UserContext);

  return user;
}

export function useUserData(): UserProfile {
  const { user } = useContext(UserContext);

  return user.data;
}

export function useUserLocation(): Partial<Location> {
  const { userLocation } = useContext(UserContext);

  return userLocation;
}

export default UserContext;
